/**
 * Purpose: Ad detail view with gallery, listing info, contact sidebar, and comments.
 * Inputs: Route params (ad slug/id), optional society-scoped URL.
 * Outputs: Full-page ad detail UI; navigates to edit/messages on user actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import ImageGallery from '../components/common/ImageGallery';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AdCardMinimal from '../components/ads/AdCardMinimal';
import { adService, messagingService } from '../services/api';
import { formatPrice, formatDate } from '../utils/helpers';
import { parseSocietyUrl, getSlugFromParams } from '../utils/urlHelpers';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';
import { canManageOwnedResource } from '../utils/resourcePermissions';

function formatCategoryLabel(category) {
  if (!category) return 'Other';
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const AdDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [ad, setAd] = useState(null);
  const [relatedAds, setRelatedAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [startingConversation, setStartingConversation] = useState(false);
  const [societySlug, setSocietySlug] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState(null);

  const adIdentifier = getSlugFromParams(params, 'ad');
  const { societySlug: urlSocietySlug } = parseSocietyUrl(location.pathname);
  const backPath = societySlug ? `/societies/${societySlug}/ads` : '/ads';

  const loadAd = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const adData = await adService.getAd(adIdentifier);
      setAd(adData);
      setViews(adData.views || 0);
    } catch (err) {
      console.error('Error loading ad:', err);
      setError('Failed to load ad details');
    } finally {
      setLoading(false);
    }
  }, [adIdentifier]);

  const loadRelatedAds = useCallback(
    async (category) => {
      try {
        const response = await adService.getAds({
          category: category || undefined,
          page_size: 6,
          exclude: adIdentifier,
        });
        setRelatedAds(response.results || response);
      } catch (err) {
        console.error('Error loading related ads:', err);
        setRelatedAds([]);
      }
    },
    [adIdentifier],
  );

  const incrementViews = useCallback(async () => {
    try {
      await adService.incrementViews(adIdentifier);
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  }, [adIdentifier]);

  const loadComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const list = await adService.getAdComments(adIdentifier);
      setComments(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading ad comments:', err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [adIdentifier]);

  useEffect(() => {
    if (!adIdentifier) return;
    setSocietySlug(urlSocietySlug);
    loadAd();
    incrementViews();
    loadComments();
  }, [adIdentifier, urlSocietySlug, loadAd, incrementViews, loadComments]);

  useEffect(() => {
    if (ad?.category) {
      loadRelatedAds(ad.category);
    }
  }, [ad?.category, loadRelatedAds]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/?auth=login');
      return;
    }
    const text = (commentText || '').trim();
    if (!text) return;
    try {
      setCommentSubmitting(true);
      setCommentError(null);
      await adService.createAdComment(adIdentifier, text);
      setCommentText('');
      await loadComments();
    } catch (err) {
      setCommentError(err?.response?.data?.message || 'Failed to post comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleEdit = () => {
    if (societySlug) {
      navigate(`/societies/${societySlug}/ads/${adIdentifier}/edit`);
    } else {
      navigate(`/ads/${adIdentifier}/edit`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    try {
      await adService.deleteAd(adIdentifier);
      navigate(backPath);
    } catch (err) {
      console.error('Error deleting ad:', err);
      alert('Failed to delete ad');
    }
  };

  const handleContactSeller = async () => {
    if (!isAuthenticated) {
      navigate('/?auth=login');
      return;
    }
    if (!adIdentifier) return;
    try {
      setStartingConversation(true);
      setError(null);
      const data = await messagingService.startConversationFromAd(adIdentifier);
      const conversationId = data?.id;
      if (conversationId) {
        navigate(`/messages/${conversationId}`);
      } else {
        setError('Could not start conversation. Please try again.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to start conversation';
      setError(msg);
    } finally {
      setStartingConversation(false);
    }
  };

  const canManage = user && ad && canManageOwnedResource(user, ad);

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingSpinner size="large" />
        </div>
      </PageLayout>
    );
  }

  if (error && !ad) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-[50vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="text-5xl mb-4" aria-hidden>⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ad Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="inline-flex items-center justify-center rounded-xl bg-emerald-600 text-white px-6 py-3 font-medium hover:bg-emerald-700 transition-colors"
            >
              Back to Ads
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!ad) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-[50vh] flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="text-5xl mb-4" aria-hidden>📄</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Ad Not Found</h2>
            <p className="text-gray-600 mb-6">The ad you&apos;re looking for doesn&apos;t exist.</p>
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="inline-flex items-center justify-center rounded-xl bg-emerald-600 text-white px-6 py-3 font-medium hover:bg-emerald-700 transition-colors"
            >
              Back to Ads
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const priceLabel = formatPrice(ad.price);
  const categoryLabel = formatCategoryLabel(ad.category);
  const societyName =
    ad.society && typeof ad.society === 'object' ? ad.society.name : ad.society;

  return (
    <PageLayout title={`${ad.title} - PakPoints`}>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
          {/* Back navigation */}
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-emerald-700 mb-4 sm:mb-6 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to listings
          </button>

          <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Gallery */}
              <ImageGallery images={ad.images || []} title="Photos" />

              {/* Mobile price strip (sidebar shows on lg+) */}
              <div className="lg:hidden rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
                <p className="text-sm font-medium text-emerald-700 mb-1">Asking price</p>
                <p className="text-3xl font-bold text-gray-900 tracking-tight">{priceLabel}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <MetaChip icon="eye" label={`${views} views`} />
                  <MetaChip icon="calendar" label={formatDate(ad.created_at)} />
                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                    {categoryLabel}
                  </span>
                </div>
              </div>

              {/* Title & description card */}
              <article className="rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
                {ad.published === false && (
                  <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-amber-900">
                      This ad is not published yet. Only you can see it until an admin approves it.
                    </p>
                  </div>
                )}

                <div className="p-5 sm:p-6">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                      {categoryLabel}
                    </span>
                    <span className="hidden sm:inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                      {formatDate(ad.created_at)}
                    </span>
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-4">
                    {ad.title}
                  </h1>

                  <section className="mb-6">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
                      Description
                    </h2>
                    <p className="text-gray-700 text-base leading-relaxed whitespace-pre-wrap">
                      {ad.description || 'No description provided.'}
                    </p>
                  </section>

                  {/* Mobile contact + actions */}
                  <div className="lg:hidden space-y-4 pt-4 border-t border-gray-100">
                    <ContactBlock ad={ad} societyName={societyName} />
                    <ActionButtons
                      canManage={canManage}
                      startingConversation={startingConversation}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onContact={handleContactSeller}
                    />
                  </div>
                </div>
              </article>

              {/* Comments */}
              <section className="rounded-2xl border border-gray-200/80 bg-white shadow-sm p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
                {commentsLoading ? (
                  <p className="text-gray-500 text-sm">Loading comments…</p>
                ) : comments.length === 0 ? (
                  <p className="text-gray-500 text-sm mb-4">No comments yet. Be the first to ask a question.</p>
                ) : (
                  <ul className="space-y-4 mb-6">
                    {comments.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3"
                      >
                        <p className="text-gray-900 text-sm sm:text-base">{c.text}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {c.user_name || 'Anonymous'} · {formatDate(c.created_at)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {isAuthenticated ? (
                  <form onSubmit={handleSubmitComment} className="space-y-3">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Ask about this item…"
                      rows={3}
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      disabled={commentSubmitting}
                    />
                    {commentError && <p className="text-red-600 text-sm">{commentError}</p>}
                    <button
                      type="submit"
                      disabled={commentSubmitting || !(commentText || '').trim()}
                      className="rounded-xl bg-emerald-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {commentSubmitting ? 'Posting…' : 'Post comment'}
                    </button>
                  </form>
                ) : (
                  <p className="text-gray-500 text-sm">
                    <button
                      type="button"
                      onClick={() => navigate('/?auth=login')}
                      className="text-emerald-600 font-medium hover:text-emerald-700"
                    >
                      Log in
                    </button>{' '}
                    to leave a comment.
                  </p>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1 mt-6 lg:mt-0 space-y-4 lg:sticky lg:top-24">
              <div className="hidden lg:block rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-emerald-700 mb-1">Asking price</p>
                <p className="text-3xl font-bold text-gray-900 tracking-tight mb-4">{priceLabel}</p>
                <div className="space-y-2 mb-6 pb-6 border-b border-gray-100">
                  <MetaChip icon="eye" label={`${views} views`} />
                  <MetaChip icon="calendar" label={formatDate(ad.created_at)} />
                </div>
                <ActionButtons
                  canManage={canManage}
                  startingConversation={startingConversation}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onContact={handleContactSeller}
                  stacked
                />
              </div>

              <div className="hidden lg:block rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-4">
                  Seller
                </h2>
                <ContactBlock ad={ad} societyName={societyName} />
              </div>
            </aside>
          </div>

          {/* Related ads */}
          {relatedAds.length > 0 && (
            <section className="mt-10 sm:mt-14">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5">Similar listings</h2>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
                {relatedAds.map((relatedAd) => (
                  <div key={relatedAd.id} className="snap-start flex-shrink-0">
                    <AdCardMinimal ad={relatedAd} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

function MetaChip({ icon, label }) {
  const icons = {
    eye: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    ),
    calendar: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    ),
  };
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        {icons[icon]}
      </svg>
      {label}
    </span>
  );
}

function ContactBlock({ ad, societyName }) {
  return (
    <ul className="space-y-3 text-sm">
      <li className="flex gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-semibold text-sm">
          {(ad.user_name || 'S').charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{ad.user_name || 'Seller'}</p>
          <p className="text-gray-500 text-xs">Posted by</p>
        </div>
      </li>
      {societyName && (
        <li className="flex items-start gap-2 text-gray-700">
          <svg className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          </svg>
          <span>{societyName}</span>
        </li>
      )}
      {ad.location && (
        <li className="flex items-start gap-2 text-gray-700">
          <GoogleMapsLocationLink
            query={ad.location}
            label={ad.location}
            className="inline-flex items-start gap-2 text-emerald-700 hover:text-emerald-800 font-medium"
          >
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <span className="underline-offset-2 hover:underline">{ad.location}</span>
          </GoogleMapsLocationLink>
        </li>
      )}
      <li>
        {ad.contact_phone ? (
          <a
            href={`tel:${ad.contact_phone}`}
            className="inline-flex items-center gap-2 font-medium text-emerald-700 hover:text-emerald-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {ad.contact_phone}
          </a>
        ) : (
          <span className="text-gray-500">Phone not provided</span>
        )}
      </li>
    </ul>
  );
}

function ActionButtons({
  canManage,
  startingConversation,
  onEdit,
  onDelete,
  onContact,
  stacked = false,
}) {
  const btnBase =
    'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-colors w-full';
  const wrap = stacked ? 'flex flex-col gap-3' : 'flex flex-wrap gap-3';

  if (canManage) {
    return (
      <div className={wrap}>
        <button type="button" onClick={onEdit} className={`${btnBase} bg-emerald-600 text-white hover:bg-emerald-700`}>
          Edit ad
        </button>
        <button type="button" onClick={onDelete} className={`${btnBase} bg-white border border-red-200 text-red-700 hover:bg-red-50`}>
          Delete ad
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onContact}
      disabled={startingConversation}
      className={`${btnBase} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shadow-sm shadow-emerald-600/20`}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
      {startingConversation ? 'Opening chat…' : 'Message seller'}
    </button>
  );
}

export default AdDetailPage;
